import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useGetOfficesCountByState } from '~/domain/Offices/usecases/useGetOfficesCountByState';
import { useGetProfessionalCountsByOffice } from '~/domain/Professionals/usecases/useGetProfessionalCountsByOffice';
import { useUserSelector } from '~/hooks/useUserSelector';
import { RemoteConfigUtils } from '~/utils/RemoteConfigUtils';
import { PROFESSIONAL_RADIUS_MILES } from './constants';
import { IUserLike } from './types';

// Stored against the singleton QueryClient so it survives Step 3
// unmount/remount when the user navigates back to Step 2 and forward.
const LAST_ADDRESS_KEY = ['_office_setup_copy_last_address'] as const;

const FALLBACK_TITLE = "Let's Finish Setting Up Your Office";

const HIGH_DENSITY_SUBTITLE_FALLBACK =
  'Offices in your area already trust GoTu to secure coverage for their practices. Let’s finish your setup.';
const LOW_DENSITY_SUBTITLE =
  'We’re actively expanding our network in your area. Be a part of the offices leading the way.';

const PROFESSIONALS_NEARBY_THRESHOLD_DEFAULT = 50;

const buildHighDensitySubtitle = (officesCount: number) =>
  // eslint-disable-next-line max-len
  `${officesCount} offices in your area trust GoTu to secure coverage for their practices. Let’s finish your setup.`;

interface IOfficeSetupCopy {
  title: string;
  subtitle: string;
}

export const useOfficeSetupCopy = (): IOfficeSetupCopy => {
  const queryClient = useQueryClient();
  const user = useUserSelector() as IUserLike | null | undefined;
  const officeId = user?.id ?? '';
  const officeState = user?.address?.[0]?.state ?? '';
  const officePlaceId = user?.address?.[0]?.place_id ?? '';

  // Both BE counts are derived from the office's stored address. The
  // professionals query is keyed only by officeId in this caller (state is
  // not passed), so a stale cache entry would survive the 5-minute
  // staleTime even after Step 2 updates the address. Compare against the
  // last-seen address recorded on the QueryClient itself — that survives
  // Step 3 unmount/remount when react-router drops the screen on back.
  const addressKey = `${officeState}|${officePlaceId}`;

  useEffect(() => {
    const previous = queryClient.getQueryData<string>(LAST_ADDRESS_KEY);

    if (previous === undefined) {
      queryClient.setQueryData(LAST_ADDRESS_KEY, addressKey);
      return;
    }

    if (previous === addressKey) return;

    queryClient.setQueryData(LAST_ADDRESS_KEY, addressKey);
    queryClient.invalidateQueries({ queryKey: ['GET_PROFESSIONAL_COUNTS'] });
    queryClient.invalidateQueries({ queryKey: ['GET_OFFICES_COUNT_BY_STATE'] });
  }, [addressKey, queryClient]);

  const {
    totalCount,
    isLoading: isLoadingProfessionals,
    isError: isErrorProfessionals,
  } = useGetProfessionalCountsByOffice(officeId, {
    distance: PROFESSIONAL_RADIUS_MILES,
  });

  // Remote Config drives the cutoff so PM can tune it without a deploy.
  // The getter returns 0 when Firebase has not resolved yet, so fall back to
  // the default here — otherwise `totalCount >= 0` would always be true.
  const remoteThreshold = RemoteConfigUtils.getProfessionalsNearbyThreshold();
  const threshold =
    remoteThreshold > 0 ? remoteThreshold : PROFESSIONALS_NEARBY_THRESHOLD_DEFAULT;

  const isHighDensity = totalCount >= threshold;

  // Only fetch the offices-in-state count when we actually need it (high density branch).
  const {
    count: officesCount,
    isLoading: isLoadingOffices,
    isError: isErrorOffices,
  } = useGetOfficesCountByState(officeState, { enable: isHighDensity });

  const showFallback = !officeId || isErrorProfessionals || isLoadingProfessionals;

  if (showFallback) {
    return { title: FALLBACK_TITLE, subtitle: HIGH_DENSITY_SUBTITLE_FALLBACK };
  }

  if (isHighDensity) {
    const officesCountReady =
      typeof officesCount === 'number' && !isLoadingOffices && !isErrorOffices;

    return {
      title: `${totalCount} Professionals Are Ready Nearby`,
      subtitle: officesCountReady
        ? buildHighDensitySubtitle(officesCount)
        : HIGH_DENSITY_SUBTITLE_FALLBACK,
    };
  }

  return {
    title: `${totalCount} Professionals Within ${PROFESSIONAL_RADIUS_MILES} Miles`,
    subtitle: LOW_DENSITY_SUBTITLE,
  };
};
